'use client';

import React, { useState, useRef, useEffect, useCallback, useMemo, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { patchProject } from '@/shell/services/api.service';
import { useAutosave, useHistory } from '@/shell/hooks';
import type { Project } from '@inno-ck/shared';
import type {
  WhiteboardCanvasProps,
  WhiteboardTool,
  WhiteboardElement,
  PenElement,
  RectElement,
  EllipseElement,
  DiamondElement,
  TriangleElement,
  LineElement,
  ArrowElement,
  StickyElement,
  ImageElement,
} from '@/types';

// ─── Type Definitions ─────────────────────────────────────────────────────────

interface Point {
  x: number;
  y: number;
}

interface BBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface PenSize {
  s: number;
  label: string;
}

interface ToolDef {
  id: WhiteboardTool;
  title: string;
  svg: ReactNode;
}

interface ResizeHandle {
  id: string;
  x: number;
  y: number;
  cur: string;
}

interface ViewState {
  x: number;
  y: number;
  scale: number;
}

interface PanState {
  sx: number;
  sy: number;
  vb0: ViewState;
}

interface DragState {
  ids: string[];
  startPt: Point;
  origs: Record<string, MutableWhiteboardElement>;
}

interface MarqueeState {
  x: number;
  y: number;
  x2: number;
  y2: number;
}

interface ResizeState {
  id: string;
  handle: string;
  startPt: Point;
  orig: MutableWhiteboardElement;
}

interface CropState {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface CropInteract {
  mode: 'move' | 'resize';
  handle?: string;
  startCrop: CropState;
  startX: number;
  startY: number;
}

interface NaturalSize {
  w: number;
  h: number;
}

interface DisplaySize {
  w: number;
  h: number;
}

// Drawing element types (mutable during drawing)
type DrawingElement =
  | (Omit<PenElement, 'id'> & { id?: string })
  | (Omit<RectElement, 'id'> & { id?: string })
  | (Omit<EllipseElement, 'id'> & { id?: string })
  | (Omit<DiamondElement, 'id'> & { id?: string })
  | (Omit<TriangleElement, 'id'> & { id?: string })
  | (Omit<LineElement, 'id'> & { id?: string })
  | (Omit<ArrowElement, 'id'> & { id?: string });

// Mutable element type for internal state
type MutableWhiteboardElement = {
  id: string;
  type: WhiteboardTool;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  x2?: number;
  y2?: number;
  pts?: Point[];
  stroke?: string;
  fill?: string;
  sw?: number;
  text?: string;
  bg?: string;
  src?: string;
  rotate?: number;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: string;
  fontStyle?: string;
  textDecoration?: string;
  textColor?: string;
};

// ─── Utilities ────────────────────────────────────────────────────────────────
const uid = (): string => Math.random().toString(36).slice(2, 9);
const ptsToD = (pts: Point[]): string =>
  pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
const diamondPts = (x: number, y: number, w: number, h: number): string =>
  `${x+w/2},${y} ${x+w},${y+h/2} ${x+w/2},${y+h} ${x},${y+h/2}`;
const triPts = (x: number, y: number, w: number, h: number): string =>
  `${x+w/2},${y} ${x+w},${y+h} ${x},${y+h}`;

function bbox(el: MutableWhiteboardElement): BBox {
  if (el.type === 'pen' && el.pts) {
    const xs = el.pts.map(p => p.x), ys = el.pts.map(p => p.y);
    return { x: Math.min(...xs), y: Math.min(...ys),
             w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
  }
  if ((el.type === 'line' || el.type === 'arrow') && el.x !== undefined && el.y !== undefined && el.x2 !== undefined && el.y2 !== undefined)
    return { x: Math.min(el.x, el.x2), y: Math.min(el.y, el.y2),
             w: Math.abs(el.x2 - el.x), h: Math.abs(el.y2 - el.y) };
  return { x: el.x ?? 0, y: el.y ?? 0, w: el.w ?? 0, h: el.h ?? 0 };
}

// ─── Palette ──────────────────────────────────────────────────────────────────
const PALETTE: string[] = [
  '#1a1a1a','#ef4444','#f97316','#eab308','#22c55e',
  '#3b82f6','#8b5cf6','#ec4899','#14b8a6','#64748b','#ffffff',
];
const STICKY_BG: string[]  = ['#fef08a','#fca5a5','#bbf7d0','#bfdbfe','#e9d5ff','#fed7aa'];
const PEN_SIZES: PenSize[]  = [{ s: 2, label: 'Fine' }, { s: 5, label: 'Moyenne' }, { s: 12, label: 'Large' }];

// ─── Tool definitions ─────────────────────────────────────────────────────────
const TOOLS: (ToolDef | null)[] = [
  { id: 'select',   title: 'Sélection (V)',  svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M4 2l16 10-7 2-3 7z"/></svg> },
  { id: 'hand',     title: 'Déplacer (H · maintiens Espace)', svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 11V6a2 2 0 0 0-4 0v5"/><path d="M14 10V4a2 2 0 0 0-4 0v7"/><path d="M10 10.5V6a2 2 0 0 0-4 0v9"/><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/></svg> },
  { id: 'pen',      title: 'Pinceau (P)',    svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/></svg> },
  { id: 'eraser',   title: 'Gomme (E)',      svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 20H7L3 16l13-13 6 6-2 2"/></svg> },
  null,
  { id: 'rect',     title: 'Rectangle (R)',  svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="1"/></svg> },
  { id: 'ellipse',  title: 'Ellipse (O)',    svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><ellipse cx="12" cy="12" rx="9" ry="6"/></svg> },
  { id: 'diamond',  title: 'Losange (D)',    svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12,3 21,12 12,21 3,12"/></svg> },
  { id: 'triangle', title: 'Triangle (T)',   svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12,4 22,20 2,20"/></svg> },
  null,
  { id: 'line',     title: 'Ligne (L)',      svg: <svg width="15" height="15" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="19" x2="19" y2="5"/></svg> },
  { id: 'arrow',    title: 'Flèche (A)',     svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="19" x2="18" y2="6"/><polyline points="9,6 18,6 18,15"/></svg> },
  null,
  { id: 'sticky',   title: 'Post-it (S)',    svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h8l6-6V4a2 2 0 0 0-2-2z"/><polyline points="14,2 14,8 20,8"/></svg> },
  { id: 'image',    title: 'Image (I)',      svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/></svg> },
];

// ─── Image import modal ───────────────────────────────────────────────────────
interface ImageImportModalProps {
  onConfirm: (src: string) => void;
  onClose: () => void;
}

function ImageImportModal({ onConfirm, onClose }: ImageImportModalProps): React.ReactElement {
  const [tab, setTab]         = useState<'file' | 'url'>('file');
  const [url, setUrl]         = useState<string>('');
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError]     = useState<string>('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Fichier non valide.'); return; }
    const reader = new FileReader();
    reader.onload = (ev): void => { 
      if (ev.target?.result) {
        setPreview(ev.target.result as string); 
        setError('');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleUrlLoad = (): void => {
    if (!url.trim()) return;
    setPreview(url.trim());
    setError('');
  };

  const handleConfirm = (): void => {
    if (!preview) { setError('Veuillez choisir une image.'); return; }
    onConfirm(preview);
  };

  const btnBase: React.CSSProperties = { padding: '5px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 12, border: 'none' };

  return createPortal(
    <div style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.4)',
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={(e): void => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:'#fff', borderRadius:12, padding:24, width:400,
        boxShadow:'0 8px 40px rgba(0,0,0,0.2)', display:'flex', flexDirection:'column', gap:14 }}
        onClick={(e): void => e.stopPropagation()}>
        <div style={{ fontWeight:700, fontSize:14, color:'#222' }}>🖼 Insérer une image</div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:0, borderBottom:'1px solid #e5e5e5' }}>
          {([['file','📂 Depuis l\'ordinateur'], ['url','🔗 Via URL']] as [string, string][]).map(([id, label]) => (
            <button key={id} onClick={(): void => setTab(id as 'file' | 'url')}
              style={{ ...btnBase, borderRadius:'6px 6px 0 0', background:'none',
                color: tab === id ? '#3b82f6' : '#666',
                borderBottom: tab === id ? '2px solid #3b82f6' : '2px solid transparent',
                fontWeight: tab === id ? 700 : 400 }}>
              {label}
            </button>
          ))}
        </div>

        {/* File upload */}
        {tab === 'file' && (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile}
              style={{ display:'none' }} />
            <button onClick={(): void => fileRef.current?.click()}
              style={{ ...btnBase, background:'#f0f4ff', color:'#3b82f6',
                border:'1.5px dashed #93c5fd', padding:'12px 0', fontSize:13 }}>
              Cliquer pour choisir un fichier image
            </button>
          </div>
        )}

        {/* URL input */}
        {tab === 'url' && (
          <div style={{ display:'flex', gap:8 }}>
            <input value={url} onChange={(e): void => setUrl(e.target.value)}
              onKeyDown={(e): void => { if (e.key === 'Enter') handleUrlLoad(); }}
              placeholder="https://…"
              style={{ flex:1, padding:'7px 10px', borderRadius:6, border:'1px solid #ddd', fontSize:12, outline:'none' }} />
            <button onClick={handleUrlLoad}
              style={{ ...btnBase, background:'#3b82f6', color:'#fff', padding:'7px 14px' }}>
              Charger
            </button>
          </div>
        )}

        {/* Preview */}
        {preview && (
          <div style={{ borderRadius:8, overflow:'hidden', border:'1px solid #e5e5e5',
            maxHeight:200, display:'flex', alignItems:'center', justifyContent:'center',
            background:'#f8f8f8' }}>
            <img src={preview} alt="preview" onError={(): void => setError('Impossible de charger l\'image.')}
              style={{ maxWidth:'100%', maxHeight:200, objectFit:'contain' }} />
          </div>
        )}

        {error && <div style={{ color:'#ef4444', fontSize:12 }}>{error}</div>}

        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ ...btnBase, background:'#f5f5f5', color:'#555', border:'1px solid #ddd' }}>Annuler</button>
          <button onClick={handleConfirm} disabled={!preview}
            style={{ ...btnBase, background: preview ? '#3b82f6' : '#93c5fd', color:'#fff', opacity: preview ? 1 : 0.7 }}>
            Insérer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Crop modal ───────────────────────────────────────────────────────────────
interface CropModalProps {
  el: ImageElement;
  onConfirm: (newSrc: string, nw: number, nh: number) => void;
  onClose: () => void;
}

function CropModal({ el, onConfirm, onClose }: CropModalProps): React.ReactElement | null {
  const MAX_W = 520, MAX_H = 380;
  const containerRef = useRef<HTMLDivElement>(null);
  const [natSize, setNatSize]   = useState<NaturalSize>({ w: 1, h: 1 });
  const [dispSize, setDispSize] = useState<DisplaySize>({ w: MAX_W, h: MAX_H });
  const [crop, setCrop]         = useState<CropState | null>(null);
  const cropInteract = useRef<CropInteract | null>(null);

  // Load image natural dimensions
  useEffect(() => {
    const img = new window.Image();
    img.onload = (): void => {
      const nat = { w: img.naturalWidth, h: img.naturalHeight };
      setNatSize(nat);
      const scale = Math.min(MAX_W / nat.w, MAX_H / nat.h, 1);
      const dw = Math.round(nat.w * scale), dh = Math.round(nat.h * scale);
      setDispSize({ w: dw, h: dh });
      setCrop({ x: 0, y: 0, w: dw, h: dh });
    };
    img.src = el.src;
  }, [el.src]);

  const clampCrop = (c: CropState, dw: number, dh: number): CropState => ({
    x: Math.max(0, Math.min(c.x, dw - 10)),
    y: Math.max(0, Math.min(c.y, dh - 10)),
    w: Math.max(10, Math.min(c.w, dw - Math.max(0, c.x))),
    h: Math.max(10, Math.min(c.h, dh - Math.max(0, c.y))),
  });

  const onOverlayDown = (e: React.MouseEvent): void => {
    e.preventDefault();
    if (!containerRef.current || !crop) return;
    const rect = containerRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left, my = e.clientY - rect.top;
    // Check if inside crop rect
    if (mx >= crop.x && mx <= crop.x + crop.w && my >= crop.y && my <= crop.y + crop.h) {
      cropInteract.current = { mode: 'move', startCrop: { ...crop }, startX: mx, startY: my };
    }
  };

  const onHandleDown = (e: React.MouseEvent, handle: string): void => {
    e.preventDefault(); e.stopPropagation();
    if (!containerRef.current || !crop) return;
    const rect = containerRef.current.getBoundingClientRect();
    cropInteract.current = {
      mode: 'resize', handle,
      startCrop: { ...crop },
      startX: e.clientX - rect.left,
      startY: e.clientY - rect.top,
    };
  };

  useEffect(() => {
    const onMove = (e: globalThis.MouseEvent): void => {
      if (!cropInteract.current || !containerRef.current || !crop) return;
      const rect = containerRef.current.getBoundingClientRect();
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      const { mode, handle, startCrop, startX, startY } = cropInteract.current;
      const dx = mx - startX, dy = my - startY;
      const dw = dispSize.w, dh = dispSize.h;

      if (mode === 'move') {
        setCrop(clampCrop({ ...startCrop, x: startCrop.x + dx, y: startCrop.y + dy }, dw, dh));
      } else if (handle) {
        let { x, y, w, h } = startCrop;
        if (handle === 'nw') { x += dx; y += dy; w -= dx; h -= dy; }
        else if (handle === 'ne') { y += dy; w += dx; h -= dy; }
        else if (handle === 'se') { w += dx; h += dy; }
        else if (handle === 'sw') { x += dx; w -= dx; h += dy; }
        else if (handle === 'n')  { y += dy; h -= dy; }
        else if (handle === 's')  { h += dy; }
        else if (handle === 'w')  { x += dx; w -= dx; }
        else if (handle === 'e')  { w += dx; }
        setCrop(clampCrop({ x, y, w, h }, dw, dh));
      }
    };
    const onUp = (): void => { cropInteract.current = null; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return (): void => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
  }, [dispSize, crop]);

  const applyCrop = (): void => {
    if (!crop) return;
    // Convert display px → natural px
    const scaleX = natSize.w / dispSize.w;
    const scaleY = natSize.h / dispSize.h;
    const nx = Math.round(crop.x * scaleX), ny = Math.round(crop.y * scaleY);
    const nw = Math.round(crop.w * scaleX), nh = Math.round(crop.h * scaleY);

    const canvas = document.createElement('canvas');
    canvas.width = nw; canvas.height = nh;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = (): void => {
      ctx.drawImage(img, nx, ny, nw, nh, 0, 0, nw, nh);
      const dataUrl = canvas.toDataURL('image/png');
      // Aspect ratio of cropped region
      onConfirm(dataUrl, nw, nh);
    };
    img.onerror = (): void => {
      // CORS issue: crop using CSS only (update src as-is, update displayed region)
      alert('Rognage impossible pour les images externes (restriction CORS). Essayez avec un fichier local.');
    };
    img.src = el.src;
  };

  if (!crop) return null;

  const HS = 8; // handle size
  const handles: ResizeHandle[] = [
    { id:'nw', x: crop.x,            y: crop.y,            cur:'nw-resize' },
    { id:'n',  x: crop.x+crop.w/2,   y: crop.y,            cur:'n-resize'  },
    { id:'ne', x: crop.x+crop.w,     y: crop.y,            cur:'ne-resize' },
    { id:'e',  x: crop.x+crop.w,     y: crop.y+crop.h/2,   cur:'e-resize'  },
    { id:'se', x: crop.x+crop.w,     y: crop.y+crop.h,     cur:'se-resize' },
    { id:'s',  x: crop.x+crop.w/2,   y: crop.y+crop.h,     cur:'s-resize'  },
    { id:'sw', x: crop.x,            y: crop.y+crop.h,     cur:'sw-resize' },
    { id:'w',  x: crop.x,            y: crop.y+crop.h/2,   cur:'w-resize'  },
  ];

  return createPortal(
    <div style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.55)',
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={(e): void => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:'#1e1e1e', borderRadius:12, padding:20,
        boxShadow:'0 12px 48px rgba(0,0,0,0.4)', display:'flex', flexDirection:'column', gap:14 }}
        onClick={(e): void => e.stopPropagation()}>
        <div style={{ color:'#fff', fontWeight:700, fontSize:13 }}>✂️ Rogner l'image</div>

        {/* Image container with crop overlay */}
        <div ref={containerRef}
          style={{ position:'relative', width: dispSize.w, height: dispSize.h,
            overflow:'hidden', cursor:'crosshair', userSelect:'none', touchAction:'none' }}
          onPointerDown={onOverlayDown}>

          <img src={el.src} alt="" draggable={false}
            style={{ width: dispSize.w, height: dispSize.h, display:'block', pointerEvents:'none' }} />

          {/* Dark overlay outside crop */}
          <svg style={{ position:'absolute', inset:0, pointerEvents:'none' }}
            width={dispSize.w} height={dispSize.h}>
            <defs>
              <mask id="crop-mask">
                <rect width={dispSize.w} height={dispSize.h} fill="white" />
                <rect x={crop.x} y={crop.y} width={crop.w} height={crop.h} fill="black" />
              </mask>
            </defs>
            <rect width={dispSize.w} height={dispSize.h} fill="rgba(0,0,0,0.55)" mask="url(#crop-mask)" />
            {/* Crop border */}
            <rect x={crop.x} y={crop.y} width={crop.w} height={crop.h}
              fill="none" stroke="#fff" strokeWidth={1.5} strokeDasharray="5 3" />
            {/* Rule-of-thirds grid */}
            {[1/3,2/3].map(f => (
              <React.Fragment key={f}>
                <line x1={crop.x + crop.w*f} y1={crop.y} x2={crop.x + crop.w*f} y2={crop.y+crop.h} stroke="rgba(255,255,255,0.35)" strokeWidth={1} />
                <line x1={crop.x} y1={crop.y+crop.h*f} x2={crop.x+crop.w} y2={crop.y+crop.h*f} stroke="rgba(255,255,255,0.35)" strokeWidth={1} />
              </React.Fragment>
            ))}
          </svg>

          {/* Resize handles */}
          {handles.map(h => (
            <div key={h.id} onPointerDown={(e): void => onHandleDown(e, h.id)}
              style={{ position:'absolute', width: HS, height: HS,
                left: h.x - HS/2, top: h.y - HS/2,
                background:'#fff', border:'1.5px solid #3b82f6', borderRadius:2,
                cursor: h.cur, zIndex:2 }} />
          ))}
        </div>

        {/* Crop info */}
        <div style={{ color:'#999', fontSize:11 }}>
          Zone : {Math.round(crop.w)} × {Math.round(crop.h)} px affiché
          &nbsp;·&nbsp; Naturel : {Math.round(crop.w * natSize.w / dispSize.w)} × {Math.round(crop.h * natSize.h / dispSize.h)} px
        </div>

        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button onClick={onClose}
            style={{ padding:'6px 16px', borderRadius:6, border:'1px solid #555', background:'#333', color:'#ccc', cursor:'pointer', fontSize:12 }}>
            Annuler
          </button>
          <button onClick={applyCrop}
            style={{ padding:'6px 16px', borderRadius:6, border:'none', background:'#3b82f6', color:'#fff', cursor:'pointer', fontSize:12, fontWeight:600 }}>
            ✂️ Rogner
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Resize handles overlay ───────────────────────────────────────────────────
interface ResizeHandlesProps {
  el: MutableWhiteboardElement;
  onHandleDown: (e: React.MouseEvent, id: string, handle: string) => void;
}

function ResizeHandles({ el, onHandleDown }: ResizeHandlesProps): React.ReactElement | null {
  const HS = 8;
  if (el.type === 'pen') return null;

  let handles: ResizeHandle[];
  if (el.type === 'line' || el.type === 'arrow') {
    handles = [
      { id:'p1', x: el.x ?? 0,  y: el.y ?? 0,  cur:'move' },
      { id:'p2', x: el.x2 ?? 0, y: el.y2 ?? 0, cur:'move' },
    ];
  } else {
    handles = [
      { id:'nw', x: el.x ?? 0,        y: el.y ?? 0,        cur:'nw-resize' },
      { id:'ne', x: (el.x ?? 0)+(el.w ?? 0),   y: el.y ?? 0,        cur:'ne-resize' },
      { id:'se', x: (el.x ?? 0)+(el.w ?? 0),   y: (el.y ?? 0)+(el.h ?? 0),   cur:'se-resize' },
      { id:'sw', x: el.x ?? 0,        y: (el.y ?? 0)+(el.h ?? 0),   cur:'sw-resize' },
    ];
  }

  return (
    <>
      {handles.map(h => (
        <rect key={h.id}
          x={h.x - HS/2} y={h.y - HS/2} width={HS} height={HS}
          fill="white" stroke="#3b82f6" strokeWidth={1.5} rx={2}
          style={{ cursor: h.cur }}
          onPointerDown={(e): void => { e.stopPropagation(); onHandleDown(e, el.id, h.id); }}
        />
      ))}
    </>
  );
}

// ─── Single element renderer ───────────────────────────────────────────────────
interface RenderElProps {
  el: MutableWhiteboardElement;
  selected: boolean;
  onDown: (e: React.MouseEvent, id: string) => void;
  onDblClick: (id: string) => void;
  isSelectMode: boolean;
  onResizeHandleDown: (e: React.MouseEvent, id: string, handle: string) => void;
}

function RenderEl({ el, selected, onDown, onDblClick, isSelectMode, onResizeHandleDown }: RenderElProps): React.ReactElement | null {
  const sw = el.sw ?? 2;
  const dash = (el as { dash?: 'dashed' | 'dotted' }).dash;
  // Dash patterns scale with stroke-width so thin AND thick strokes read clearly.
  //   - Dashed: butt linecap (otherwise round caps swallow the gap on thick strokes)
  //   - Dotted: round linecap turns zero-length segments into proper circles
  const dashProps = dash === 'dashed'
    ? { strokeDasharray: `${sw * 3} ${sw * 2.2}`, strokeLinecap: 'butt' as const }
    : dash === 'dotted'
      ? { strokeDasharray: `0 ${sw * 2.4}`, strokeLinecap: 'round' as const }
      : {};
  const interactProps = {
    onPointerDown: (e: React.MouseEvent): void => { e.stopPropagation(); onDown(e, el.id); },
    onDoubleClick: (e: React.MouseEvent): void => { e.stopPropagation(); onDblClick(el.id); },
    style: { cursor: isSelectMode ? 'move' : 'crosshair' } as React.CSSProperties,
  };
  const hitW = Math.max(sw, 10);

  let shape: React.ReactElement;
  switch (el.type) {
    case 'pen':
      if (!el.pts) return null;
      shape = <path d={ptsToD(el.pts)} fill="none" stroke={el.stroke} strokeWidth={sw}
        strokeLinecap="round" strokeLinejoin="round" {...dashProps} {...interactProps} />;
      break;
    case 'rect': {
      // 'transparent' instead of 'none' so the interior captures pointer events
      const visFill = el.fill && el.fill !== 'none' ? el.fill : 'transparent';
      const x = el.x ?? 0;
      const y = el.y ?? 0;
      const w = el.w ?? 0;
      const h = el.h ?? 0;
      shape = (
        <g {...interactProps}>
          <rect x={x} y={y} width={w} height={h}
            fill={visFill} stroke={el.stroke} strokeWidth={sw} rx={2}
            {...dashProps}
            style={{cursor:isSelectMode?'move':'crosshair'}} pointerEvents="all" />
          {el.text && (
            <foreignObject x={x+6} y={y+6} width={Math.max(0,w-12)} height={Math.max(0,h-12)}>
              <div style={{width:'100%',height:'100%',
                display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:el.fontSize||13,fontFamily:el.fontFamily||'sans-serif',
                fontWeight:el.fontWeight||'normal',fontStyle:el.fontStyle||'normal',
                textDecoration:el.textDecoration||'none',
                color:el.textColor||'#1a1a1a',
                wordBreak:'break-word',textAlign:'center',pointerEvents:'none',overflow:'hidden'}}>
                {el.text}
              </div>
            </foreignObject>
          )}
        </g>
      );
      break;
    }
    case 'ellipse': {
      const visFill = el.fill && el.fill !== 'none' ? el.fill : 'transparent';
      const x = el.x ?? 0;
      const y = el.y ?? 0;
      const w = el.w ?? 0;
      const h = el.h ?? 0;
      shape = (
        <g {...interactProps}>
          <ellipse cx={x+w/2} cy={y+h/2} rx={Math.abs(w/2)} ry={Math.abs(h/2)}
            fill={visFill} stroke={el.stroke} strokeWidth={sw}
            {...dashProps}
            style={{cursor:isSelectMode?'move':'crosshair'}} pointerEvents="all" />
          {el.text && (
            <foreignObject x={x+w*0.15} y={y+h*0.15} width={w*0.7} height={h*0.7}>
              <div style={{width:'100%',height:'100%',
                display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:el.fontSize||13,fontFamily:el.fontFamily||'sans-serif',
                fontWeight:el.fontWeight||'normal',fontStyle:el.fontStyle||'normal',
                textDecoration:el.textDecoration||'none',
                color:el.textColor||'#1a1a1a',
                wordBreak:'break-word',textAlign:'center',pointerEvents:'none',overflow:'hidden'}}>
                {el.text}
              </div>
            </foreignObject>
          )}
        </g>
      );
      break;
    }
    case 'diamond': {
      const visFill = el.fill && el.fill !== 'none' ? el.fill : 'transparent';
      const x = el.x ?? 0;
      const y = el.y ?? 0;
      const w = el.w ?? 0;
      const h = el.h ?? 0;
      shape = (
        <g {...interactProps}>
          <polygon points={diamondPts(x,y,w,h)}
            fill={visFill} stroke={el.stroke} strokeWidth={sw}
            {...dashProps}
            style={{cursor:isSelectMode?'move':'crosshair'}} pointerEvents="all" />
          {el.text && (
            <foreignObject x={x+w*0.2} y={y+h*0.25} width={w*0.6} height={h*0.5}>
              <div style={{width:'100%',height:'100%',
                display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:el.fontSize||13,fontFamily:el.fontFamily||'sans-serif',
                fontWeight:el.fontWeight||'normal',fontStyle:el.fontStyle||'normal',
                textDecoration:el.textDecoration||'none',
                color:el.textColor||'#1a1a1a',
                wordBreak:'break-word',textAlign:'center',pointerEvents:'none',overflow:'hidden'}}>
                {el.text}
              </div>
            </foreignObject>
          )}
        </g>
      );
      break;
    }
    case 'triangle': {
      const visFill = el.fill && el.fill !== 'none' ? el.fill : 'transparent';
      const x = el.x ?? 0;
      const y = el.y ?? 0;
      const w = el.w ?? 0;
      const h = el.h ?? 0;
      shape = (
        <g {...interactProps}>
          <polygon points={triPts(x,y,w,h)}
            fill={visFill} stroke={el.stroke} strokeWidth={sw}
            {...dashProps}
            style={{cursor:isSelectMode?'move':'crosshair'}} pointerEvents="all" />
          {el.text && (
            <foreignObject x={x+w*0.2} y={y+h*0.5} width={w*0.6} height={h*0.4}>
              <div style={{width:'100%',height:'100%',
                display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:el.fontSize||13,fontFamily:el.fontFamily||'sans-serif',
                fontWeight:el.fontWeight||'normal',fontStyle:el.fontStyle||'normal',
                textDecoration:el.textDecoration||'none',
                color:el.textColor||'#1a1a1a',
                wordBreak:'break-word',textAlign:'center',pointerEvents:'none',overflow:'hidden'}}>
                {el.text}
              </div>
            </foreignObject>
          )}
        </g>
      );
      break;
    }
    case 'line':
      if (el.x === undefined || el.y === undefined || el.x2 === undefined || el.y2 === undefined) return null;
      shape = (
        <g {...interactProps}>
          <line x1={el.x} y1={el.y} x2={el.x2} y2={el.y2} stroke="transparent" strokeWidth={hitW} />
          <line x1={el.x} y1={el.y} x2={el.x2} y2={el.y2} stroke={el.stroke} strokeWidth={sw} strokeLinecap="round" {...dashProps} style={{pointerEvents:'none'}} />
        </g>
      );
      break;
    case 'arrow': {
      if (el.x === undefined || el.y === undefined || el.x2 === undefined || el.y2 === undefined || !el.stroke) return null;
      const mid = `wbah-${el.stroke.replace('#','')}`;
      shape = (
        <g {...interactProps}>
          <line x1={el.x} y1={el.y} x2={el.x2} y2={el.y2} stroke="transparent" strokeWidth={hitW} />
          <line x1={el.x} y1={el.y} x2={el.x2} y2={el.y2} stroke={el.stroke} strokeWidth={sw}
            strokeLinecap="round" markerEnd={`url(#${mid})`} {...dashProps} style={{pointerEvents:'none'}} />
        </g>
      );
      break;
    }
    case 'image': {
      if (el.x === undefined || el.y === undefined || el.w === undefined || el.h === undefined || !el.src) return null;
      const rot = el.rotate || 0;
      const cx = el.x + el.w / 2, cy = el.y + el.h / 2;
      // When rotated 90/270°, swap w/h for the bounding box but keep display dims
      const needsSwap = rot === 90 || rot === 270;
      const dw = needsSwap ? el.h : el.w;
      const dh = needsSwap ? el.w : el.h;
      shape = (
        <g transform={`rotate(${rot},${cx},${cy})`} {...interactProps}>
          <image href={el.src} x={cx - dw/2} y={cy - dh/2} width={dw} height={dh}
            preserveAspectRatio="xMidYMid meet" style={{ cursor: isSelectMode ? 'move' : 'crosshair' }} />
        </g>
      );
      break;
    }
    case 'sticky': {
      const x = el.x ?? 0;
      const y = el.y ?? 0;
      const w = el.w ?? 160;
      const h = el.h ?? 120;
      shape = (
        <g transform={`translate(${x},${y})`} {...interactProps}>
          <rect width={w} height={h} fill={el.bg||'#fef08a'} rx={4}
            stroke={selected?'#3b82f6':'rgba(0,0,0,0.12)'} strokeWidth={selected?2:1}
            style={{filter:'drop-shadow(1px 3px 6px rgba(0,0,0,0.14))'}} />
          <foreignObject x={7} y={7} width={w-14} height={h-14}>
            <div
              style={{fontSize:12.5, fontFamily:'sans-serif', wordBreak:'break-word',
                whiteSpace:'pre-wrap', color:'#1a1a1a', userSelect:'none',
                height:'100%', overflow:'hidden', pointerEvents:'none', lineHeight:1.45}}>
              {el.text||<span style={{opacity:.38}}>Double-clic pour écrire…</span>}
            </div>
          </foreignObject>
        </g>
      );
      break;
    }
    default: return null;
  }

  const bb = selected && el.type !== 'sticky' ? bbox(el) : null;
  return (
    <g>
      {shape}
      {bb && el.type !== 'image' && (
        <rect x={bb.x-5} y={bb.y-5} width={bb.w+10} height={bb.h+10}
          fill="none" stroke="#3b82f6" strokeWidth={1.2} strokeDasharray="5 3" rx={2}
          style={{pointerEvents:'none'}} />
      )}
      {selected && el.type === 'image' && el.x !== undefined && el.y !== undefined && el.w !== undefined && el.h !== undefined && (
        <rect x={el.x-2} y={el.y-2} width={el.w+4} height={el.h+4}
          fill="none" stroke="#3b82f6" strokeWidth={1.5} strokeDasharray="5 3" rx={2}
          style={{pointerEvents:'none'}} />
      )}
      {selected && <ResizeHandles el={el} onHandleDown={onResizeHandleDown} />}
    </g>
  );
}

// ─── Drawing ghost ────────────────────────────────────────────────────────────
interface DrawingGhostProps {
  el: DrawingElement | null;
}

function DrawingGhost({ el }: DrawingGhostProps): React.ReactElement | null {
  if (!el) return null;
  const sw = el.sw ?? 2;
  const g = { style:{pointerEvents:'none' as const}, strokeDasharray:'5 3' };
  switch (el.type) {
    case 'pen':
      if (!el.pts) return null;
      return <path d={ptsToD(el.pts)} fill="none" stroke={el.stroke} strokeWidth={sw}
        strokeLinecap="round" strokeLinejoin="round" style={{pointerEvents:'none'}} />;
    case 'rect': {
      const x = (el.w ?? 0) >= 0 ? el.x : (el.x ?? 0) + (el.w ?? 0);
      const y = (el.h ?? 0) >= 0 ? el.y : (el.y ?? 0) + (el.h ?? 0);
      return <rect x={x} y={y} width={Math.abs(el.w ?? 0)} height={Math.abs(el.h ?? 0)}
        fill={el.fill||'none'} stroke={el.stroke} strokeWidth={sw} rx={2} {...g} />;
    }
    case 'ellipse': {
      const cx = (el.x ?? 0) + (el.w ?? 0)/2;
      const cy = (el.y ?? 0) + (el.h ?? 0)/2;
      return <ellipse cx={cx} cy={cy} rx={Math.abs((el.w ?? 0)/2)} ry={Math.abs((el.h ?? 0)/2)}
        fill={el.fill||'none'} stroke={el.stroke} strokeWidth={sw} {...g} />;
    }
    case 'diamond': {
      const nx = (el.w ?? 0) >= 0 ? el.x : (el.x ?? 0) + (el.w ?? 0);
      const ny = (el.h ?? 0) >= 0 ? el.y : (el.y ?? 0) + (el.h ?? 0);
      return <polygon points={diamondPts(nx??0,ny??0,Math.abs(el.w??0),Math.abs(el.h??0))}
        fill={el.fill||'none'} stroke={el.stroke} strokeWidth={sw} {...g} />;
    }
    case 'triangle': {
      const nx = (el.w ?? 0) >= 0 ? el.x : (el.x ?? 0) + (el.w ?? 0);
      const ny = (el.h ?? 0) >= 0 ? el.y : (el.y ?? 0) + (el.h ?? 0);
      return <polygon points={triPts(nx??0,ny??0,Math.abs(el.w??0),Math.abs(el.h??0))}
        fill={el.fill||'none'} stroke={el.stroke} strokeWidth={sw} {...g} />;
    }
    case 'line':
    case 'arrow':
      if (el.x === undefined || el.y === undefined || el.x2 === undefined || el.y2 === undefined) return null;
      return <line x1={el.x} y1={el.y} x2={el.x2} y2={el.y2}
        stroke={el.stroke} strokeWidth={sw} strokeLinecap="round" {...g} />;
    default:
      return null;
  }
}

// ─── Sticky editor modal ──────────────────────────────────────────────────────
interface StickyEditorProps {
  el: MutableWhiteboardElement;
  onSave: (text: string) => void;
  onClose: () => void;
}

function StickyEditor({ el, onSave, onClose }: StickyEditorProps): React.ReactElement {
  const [text, setText] = useState<string>(el.text || '');
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return (): void => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,0.25)',
      display:'flex',alignItems:'center',justifyContent:'center'}}
      onClick={(e): void => { if (e.target===e.currentTarget) onSave(text); }}>
      <div style={{background:el.bg||'#fef08a',borderRadius:10,padding:18,
        boxShadow:'0 8px 32px rgba(0,0,0,0.22)',width:300,display:'flex',flexDirection:'column',gap:12}}
        onClick={(e): void => e.stopPropagation()}>
        <div style={{fontSize:12,fontWeight:700,color:'#555',letterSpacing:'.04em'}}>
          {el.type==='sticky' ? '📝 POST-IT' : '✏️ Texte de la forme'}
        </div>
        <textarea autoFocus value={text} onChange={(e): void => setText(e.target.value)}
          placeholder="Saisir votre texte…"
          style={{resize:'none',border:'1px solid rgba(0,0,0,0.15)',borderRadius:6,
            padding:'8px 10px',fontSize:13.5,fontFamily:'sans-serif',height:130,
            background:'rgba(255,255,255,0.55)',outline:'none',lineHeight:1.5}} />
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button onClick={onClose} style={{padding:'5px 16px',border:'1px solid #ccc',borderRadius:6,background:'#fff',cursor:'pointer',fontSize:12}}>Annuler</button>
          <button onClick={(): void => onSave(text)} style={{padding:'5px 16px',border:'none',borderRadius:6,background:'#3b82f6',color:'#fff',cursor:'pointer',fontSize:12}}>OK</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function WhiteboardCanvas({ project }: WhiteboardCanvasProps): React.ReactElement {
  const [elements, setElements]       = useState<MutableWhiteboardElement[]>(() => 
    (project?.jsonData?.whiteboardElements as MutableWhiteboardElement[] | undefined) ?? []);
  const [tool, setTool]               = useState<WhiteboardTool>('select');
  const [stroke, setStroke]           = useState<string>('#1a1a1a');
  const [fill, setFill]               = useState<string>('none');
  const [sw, setSw]                   = useState<number>(2);
  const [stickyBg, setStickyBg]       = useState<string>(STICKY_BG[0] ?? '#fef08a');
  const [selectedId, setSelectedId]   = useState<string | null>(null);
  const [multiIds, setMultiIds]       = useState<Set<string>>(() => new Set());
  const [marquee, setMarquee]         = useState<MarqueeState | null>(null);
  const [lockedGroup, setLockedGroup] = useState<boolean>(false);
  const [editingEl, setEditingEl]     = useState<MutableWhiteboardElement | null>(null);
  const [drawing, setDrawing]         = useState<DrawingElement | null>(null);
  // Mirror of `drawing` kept in a ref. The in-progress gesture is driven from
  // this ref (not React state) so move/up handlers always see the live value —
  // never a stale closure from a render that hasn't committed yet. This is what
  // makes drawing reliable on touch/tablets (the tldraw/draw.io approach).
  const drawingRef = useRef<DrawingElement | null>(null);
  const [showImgModal, setShowImgModal] = useState<boolean>(false);
  const [pendingImgPt, setPendingImgPt] = useState<Point | null>(null);
  const [cropTarget, setCropTarget]   = useState<ImageElement | null>(null);

  // Pan / zoom
  const [vb, setVb] = useState<ViewState>({ x: 0, y: 0, scale: 1 });
  const panRef    = useRef<PanState | null>(null);
  const dragRef   = useRef<DragState | null>(null);
  const resizeRef = useRef<ResizeState | null>(null);
  const svgRef    = useRef<SVGSVGElement | null>(null);
  // Track the live element size so the viewBox aspect ratio always matches the
  // SVG box (no preserveAspectRatio letterboxing) on mount, resize and rotation.
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 1200, h: 800 });

  const clearSelection = useCallback((): void => {
    setSelectedId(null);
    setMultiIds(new Set());
    setMarquee(null);
    setLockedGroup(false);
  }, []);

  // Auto-save: debounced patchProject over the latest elements list.
  const { schedule: scheduleSave } = useAutosave<MutableWhiteboardElement[]>(
    els => {
      if (!project?.id) return;
      patchProject(project.id, { jsonData: { ...project?.jsonData, whiteboardElements: els } });
    },
    1500,
  );

  // Undo / redo — JSON-serialised snapshots of past element arrays. The future
  // stack is cleared on every new mutation so redo only survives until the
  // next edit.
  const history = useHistory<MutableWhiteboardElement[]>(setElements, {
    onRestore: next => { scheduleSave(next); clearSelection(); },
  });
  const { push: pushHistory, undo, redo, reset: resetHistory, canUndo, canRedo } = history;

  // Sync when project changes
  useEffect(() => {
    setElements((project?.jsonData?.whiteboardElements as MutableWhiteboardElement[] | undefined) ?? []);
    clearSelection();
    drawingRef.current = null;
    setDrawing(null);
    resetHistory();
  }, [project?.id, resetHistory, clearSelection]);

  const mutate = useCallback((fn: (prev: MutableWhiteboardElement[]) => MutableWhiteboardElement[]): void => {
    setElements(prev => {
      const next = fn([...prev]);
      pushHistory(prev);
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave, pushHistory]);

  // Keyboard shortcuts: Cmd/Ctrl+Z = undo, Cmd/Ctrl+Shift+Z or Ctrl+Y = redo
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement | null)?.isContentEditable) return;
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  // SVG coordinate from mouse event
  // Convert a client (screen) point to SVG user coordinates using the live
  // screen-CTM. This is exact regardless of viewBox / preserveAspectRatio /
  // element size — the robust approach used by draw.io (mxGraph). The previous
  // manual math drifted whenever the viewBox aspect ratio didn't match the
  // element (initial render, window resize, tablet rotation), so the drawn
  // point landed away from the cursor/finger.
  const toSvgPt = useCallback((e: MouseEvent | React.MouseEvent): Point => {
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const r = pt.matrixTransform(ctm.inverse());
    return { x: r.x, y: r.y };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const kd = (e: KeyboardEvent): void => {
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const ids = new Set<string>(multiIds);
        if (selectedId) ids.add(selectedId);
        if (ids.size === 0) return;
        mutate(els => els.filter(el => !ids.has(el.id)));
        clearSelection();
        return;
      }
      if (e.key === 'Escape') { clearSelection(); return; }
      const map: Record<string, WhiteboardTool> = { v:'select', h:'hand', p:'pen', e:'eraser', r:'rect', o:'ellipse',
                    d:'diamond', t:'triangle', l:'line', a:'arrow', s:'sticky', i:'image' };
      const val = map[e.key];
      if (val) setTool(val);
    };
    window.addEventListener('keydown', kd);
    return (): void => window.removeEventListener('keydown', kd);
  }, [selectedId, multiIds, mutate, clearSelection]);

  // Wheel zoom
  const onWheel = useCallback((e: WheelEvent): void => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.12 : 0.9;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = e.clientX - rect.left, my = e.clientY - rect.top;
    setVb(v => {
      const s = Math.max(0.15, Math.min(5, v.scale * factor));
      return { x: v.x + mx/v.scale - mx/s, y: v.y + my/v.scale - my/s, scale: s };
    });
  }, []);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return (): void => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  // Keep the viewBox in sync with the actual element size.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const update = (): void => setSize({ w: el.clientWidth || 1200, h: el.clientHeight || 800 });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return (): void => ro.disconnect();
  }, []);

  // Single-pointer gesture lock. A gesture (draw / pan / drag / resize) is owned
  // by exactly one pointer — the first finger / pen / mouse to go down. Other
  // pointers (second finger, palm) are ignored, so two fingers no longer draw a
  // line between them or size a shape by their gap. The owning pointer is also
  // captured to the SVG so all its move/up events land here even on touch.
  const activePointer = useRef<number | null>(null);
  const pidOf = (e: React.MouseEvent): number => (e.nativeEvent as PointerEvent).pointerId;
  // Try to start a gesture with this pointer. Returns false if another pointer
  // already owns the current gesture (→ caller should ignore the event).
  const beginPointer = useCallback((e: React.MouseEvent): boolean => {
    const pid = pidOf(e);
    if (activePointer.current !== null && activePointer.current !== pid) return false;
    activePointer.current = pid;
    if (pid != null) { try { svgRef.current?.setPointerCapture(pid); } catch { /* unsupported */ } }
    return true;
  }, []);
  // True unless the event comes from a non-owning secondary pointer.
  const isActivePointer = (e: React.MouseEvent): boolean =>
    activePointer.current === null || activePointer.current === pidOf(e);
  const endPointer = (): void => { activePointer.current = null; };

  // Two-finger pinch/zoom. We disable native touch gestures (touch-action:none),
  // so we implement zoom+pan ourselves like draw.io/tldraw. `pointers` holds all
  // pointers currently down on the canvas; with two of them we zoom/pan instead
  // of drawing.
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchRef = useRef<{ dist: number; mx: number; my: number } | null>(null);
  const twoPointerMetrics = (): { dist: number; mx: number; my: number } => {
    const pts = [...pointers.current.values()].slice(0, 2);
    if (pts.length < 2) return { dist: 0, mx: 0, my: 0 };
    const [a, b] = pts as [{ x: number; y: number }, { x: number; y: number }];
    return { dist: Math.hypot(a.x - b.x, a.y - b.y), mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
  };

  // Update both the gesture ref (read by move/up) and the render state.
  const applyDrawing = useCallback((next: DrawingElement | null | ((d: DrawingElement | null) => DrawingElement | null)): void => {
    const v = typeof next === 'function' ? next(drawingRef.current) : next;
    drawingRef.current = v;
    setDrawing(v);
  }, []);

  // ── Pointer down ──
  const onSvgDown = useCallback((e: React.MouseEvent): void => {
    if (e.button !== 0) return;
    const pid = pidOf(e);
    pointers.current.set(pid, { x: e.clientX, y: e.clientY });
    try { svgRef.current?.setPointerCapture(pid); } catch { /* unsupported */ }

    // Second finger on the canvas → switch to pinch zoom/pan, discarding any
    // single-finger gesture that just started.
    if (pointers.current.size >= 2) {
      applyDrawing(null);
      setMarquee(null);
      panRef.current = null;
      activePointer.current = null;
      pinchRef.current = twoPointerMetrics();
      return;
    }
    // An element drag/resize already owns input → ignore stray extra pointers.
    if (dragRef.current || resizeRef.current) return;

    activePointer.current = pid;
    const pt = toSvgPt(e);

    // Pan: alt-click, space held, or hand tool active
    if (e.altKey || tool === 'hand') {
      panRef.current = { sx: e.clientX, sy: e.clientY, vb0: { ...vb } };
      return;
    }

    if (tool === 'select') {
      // Empty canvas drag → start a marquee selection. Click without drag
      // (handled on mouseup if no movement) will just clear the selection.
      clearSelection();
      setMarquee({ x: pt.x, y: pt.y, x2: pt.x, y2: pt.y });
      return;
    }
    if (tool === 'pen')    { applyDrawing({ type:'pen', pts:[pt], stroke, sw }); return; }
    if (tool === 'eraser') return;

    if (['rect','ellipse','diamond','triangle'].includes(tool)) {
      applyDrawing({ type:tool as 'rect'|'ellipse'|'diamond'|'triangle', x:pt.x, y:pt.y, w:0, h:0, stroke, fill, sw });
      return;
    }
    if (tool === 'line' || tool === 'arrow') {
      applyDrawing({ type:tool as 'line'|'arrow', x:pt.x, y:pt.y, x2:pt.x, y2:pt.y, stroke, sw });
      return;
    }
    if (tool === 'sticky') {
      const newEl: MutableWhiteboardElement = { id:uid(), type:'sticky', x:pt.x-80, y:pt.y-60, w:160, h:120, text:'', bg:stickyBg };
      mutate(els => [...els, newEl]);
      setSelectedId(newEl.id);
      setEditingEl(newEl);
      setTool('select');
      return;
    }
    if (tool === 'image') {
      setPendingImgPt(pt);
      setShowImgModal(true);
    }
  }, [tool, toSvgPt, stroke, fill, sw, stickyBg, vb, mutate, clearSelection, applyDrawing]);

  // ── Pointer move ──
  const onSvgMove = useCallback((e: React.MouseEvent): void => {
    const pid = pidOf(e);
    if (pointers.current.has(pid)) pointers.current.set(pid, { x: e.clientX, y: e.clientY });

    // Two-finger pinch: zoom around the fingers' midpoint and pan with it.
    if (pinchRef.current && pointers.current.size >= 2) {
      const prev = pinchRef.current;
      const m = twoPointerMetrics();
      const rect = svgRef.current?.getBoundingClientRect();
      if (rect && prev.dist > 0 && m.dist > 0) {
        const factor = m.dist / prev.dist;
        setVb(v => {
          const s = Math.max(0.15, Math.min(5, v.scale * factor));
          const wx = v.x + (prev.mx - rect.left) / v.scale;
          const wy = v.y + (prev.my - rect.top) / v.scale;
          return { x: wx - (m.mx - rect.left) / s, y: wy - (m.my - rect.top) / s, scale: s };
        });
      }
      pinchRef.current = m;
      return;
    }

    if (!isActivePointer(e)) return;
    if (panRef.current) {
      const { sx, sy, vb0 } = panRef.current;
      const dx = (e.clientX-sx)/vb0.scale, dy = (e.clientY-sy)/vb0.scale;
      setVb({ ...vb0, x: vb0.x-dx, y: vb0.y-dy });
      return;
    }
    if (resizeRef.current) {
      const { id, handle, orig, startPt } = resizeRef.current;
      const pt = toSvgPt(e);
      const dx = pt.x - startPt.x, dy = pt.y - startPt.y;
      setElements(prev => prev.map(el => {
        if (el.id !== id) return el;
        if ((el.type === 'line' || el.type === 'arrow') && orig.x !== undefined && orig.y !== undefined && orig.x2 !== undefined && orig.y2 !== undefined) {
          if (handle === 'p1') return { ...el, x: orig.x+dx, y: orig.y+dy };
          if (handle === 'p2') return { ...el, x2: orig.x2+dx, y2: orig.y2+dy };
        }
        if (orig.x === undefined || orig.y === undefined || orig.w === undefined || orig.h === undefined) return el;
        let { x, y, w, h } = orig;
        if (handle==='nw') { x+=dx; y+=dy; w-=dx; h-=dy; }
        else if (handle==='ne') { y+=dy; w+=dx; h-=dy; }
        else if (handle==='se') { w+=dx; h+=dy; }
        else if (handle==='sw') { x+=dx; w-=dx; h+=dy; }
        return { ...el, x, y, w: Math.max(10,w), h: Math.max(10,h) };
      }));
      return;
    }
    if (dragRef.current) {
      const { ids, startPt, origs } = dragRef.current;
      const pt = toSvgPt(e);
      const dx = pt.x-startPt.x, dy = pt.y-startPt.y;
      const draggedSet = new Set(ids);
      setElements(prev => prev.map(el => {
        if (!draggedSet.has(el.id)) return el;
        const orig = origs[el.id];
        if (!orig) return el;
        if (el.type === 'pen' && orig.pts)
          return { ...el, pts: orig.pts.map(p => ({ x:p.x+dx, y:p.y+dy })) };
        if ((el.type === 'line' || el.type === 'arrow') && orig.x !== undefined && orig.y !== undefined && orig.x2 !== undefined && orig.y2 !== undefined)
          return { ...el, x:orig.x+dx, y:orig.y+dy, x2:orig.x2+dx, y2:orig.y2+dy };
        if (orig.x !== undefined && orig.y !== undefined)
          return { ...el, x:orig.x+dx, y:orig.y+dy };
        return el;
      }));
      return;
    }
    if (marquee) {
      const pt = toSvgPt(e);
      setMarquee(m => m ? { ...m, x2: pt.x, y2: pt.y } : null);
      return;
    }
    const dr = drawingRef.current;
    if (!dr) return;
    const pt = toSvgPt(e);
    if (dr.type === 'pen' && 'pts' in dr && dr.pts)
      applyDrawing(d => d && 'pts' in d ? { ...d, pts: [...(d.pts ?? []), pt] } : null);
    else if (['rect','ellipse','diamond','triangle'].includes(dr.type) && 'x' in dr)
      applyDrawing(d => d && 'x' in d ? { ...d, w: pt.x - (d.x ?? 0), h: pt.y - (d.y ?? 0) } : null);
    else if (dr.type === 'line' || dr.type === 'arrow')
      applyDrawing(d => d ? { ...d, x2:pt.x, y2:pt.y } : null);
  }, [toSvgPt, marquee, applyDrawing]);

  // Bounding box of any element (used for marquee intersection).
  const getBBox = useCallback((el: MutableWhiteboardElement): { x: number; y: number; w: number; h: number } | null => {
    if (el.type === 'pen' && el.pts && el.pts.length > 0) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const p of el.pts) {
        if (p.x < minX) minX = p.x; if (p.y < minY) minY = p.y;
        if (p.x > maxX) maxX = p.x; if (p.y > maxY) maxY = p.y;
      }
      return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    }
    if ((el.type === 'line' || el.type === 'arrow') && el.x !== undefined && el.x2 !== undefined && el.y !== undefined && el.y2 !== undefined) {
      const x = Math.min(el.x, el.x2), y = Math.min(el.y, el.y2);
      return { x, y, w: Math.abs(el.x2 - el.x), h: Math.abs(el.y2 - el.y) };
    }
    if (el.x !== undefined && el.y !== undefined && el.w !== undefined && el.h !== undefined) {
      return { x: el.x, y: el.y, w: el.w, h: el.h };
    }
    return null;
  }, []);

  // Union bbox of all currently selected elements — source of truth for both
  // the visual "group" frame and the invisible drag-capture rectangle.
  const selectionBBox = useMemo((): { x: number; y: number; w: number; h: number } | null => {
    const ids = new Set<string>(multiIds);
    if (selectedId) ids.add(selectedId);
    if (ids.size < 1) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const el of elements) {
      if (!ids.has(el.id)) continue;
      const bb = getBBox(el);
      if (!bb) continue;
      if (bb.x < minX) minX = bb.x;
      if (bb.y < minY) minY = bb.y;
      if (bb.x + bb.w > maxX) maxX = bb.x + bb.w;
      if (bb.y + bb.h > maxY) maxY = bb.y + bb.h;
    }
    if (!isFinite(minX)) return null;
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }, [selectedId, multiIds, elements, getBBox]);

  const multiSelectActive = (selectedId ? 1 : 0) + multiIds.size >= 2;

  // Start dragging the whole selection as a group — used both by the union
  // frame overlay and directly when the user clicks on an already-selected
  // element (handled in onElDown).
  const startGroupDrag = useCallback((e: React.MouseEvent): void => {
    const ids = new Set<string>(multiIds);
    if (selectedId) ids.add(selectedId);
    if (ids.size === 0) return;
    e.stopPropagation();
    const origs: Record<string, MutableWhiteboardElement> = {};
    for (const eid of ids) {
      const orig = elements.find(el => el.id === eid);
      if (orig) origs[eid] = JSON.parse(JSON.stringify(orig));
    }
    const finalIds = Object.keys(origs);
    if (finalIds.length === 0) return;
    if (!beginPointer(e)) return;
    dragRef.current = { ids: finalIds, startPt: toSvgPt(e), origs };
  }, [selectedId, multiIds, elements, toSvgPt, beginPointer]);

  // ── Mouse up ──
  const onSvgUp = useCallback((e?: React.MouseEvent): void => {
    if (e) {
      const pid = pidOf(e);
      pointers.current.delete(pid);
      try { svgRef.current?.releasePointerCapture(pid); } catch { /* ignore */ }
    }
    // Finishing/continuing a pinch: never commit a drawing, and don't let a
    // lone remaining finger start one.
    if (pinchRef.current) {
      pinchRef.current = pointers.current.size >= 2 ? twoPointerMetrics() : null;
      activePointer.current = null;
      return;
    }
    if (e && !isActivePointer(e)) return;   // a secondary pointer lifted — keep drawing
    endPointer();
    panRef.current = null;
    if (resizeRef.current) { resizeRef.current = null; scheduleSave(elements); return; }
    if (dragRef.current)   { dragRef.current   = null; scheduleSave(elements); return; }
    if (marquee) {
      const x1 = Math.min(marquee.x, marquee.x2);
      const y1 = Math.min(marquee.y, marquee.y2);
      const x2 = Math.max(marquee.x, marquee.x2);
      const y2 = Math.max(marquee.y, marquee.y2);
      const w = x2 - x1, h = y2 - y1;
      // Treat tiny boxes as a plain click: just clear the selection.
      if (w < 3 && h < 3) { setMarquee(null); return; }
      const hitIds: string[] = [];
      for (const el of elements) {
        const bb = getBBox(el);
        if (!bb) continue;
        const overlap = bb.x < x2 && bb.x + bb.w > x1 && bb.y < y2 && bb.y + bb.h > y1;
        if (overlap) hitIds.push(el.id);
      }
      if (hitIds.length === 0) { setMarquee(null); return; }
      const [first, ...rest] = hitIds;
      setSelectedId(first ?? null);
      setMultiIds(new Set(rest));
      setMarquee(null);
      return;
    }
    const dr = drawingRef.current;
    if (!dr) return;

    const newEl = { ...dr, id: uid() } as MutableWhiteboardElement;
    if (['rect','ellipse','diamond','triangle'].includes(newEl.type)) {
      if ((newEl.w ?? 0) < 0) { newEl.x = (newEl.x ?? 0) + (newEl.w ?? 0); newEl.w = -(newEl.w ?? 0); }
      if ((newEl.h ?? 0) < 0) { newEl.y = (newEl.y ?? 0) + (newEl.h ?? 0); newEl.h = -(newEl.h ?? 0); }
      if ((newEl.w ?? 0) < 4 || (newEl.h ?? 0) < 4) { applyDrawing(null); return; }
    }
    if (newEl.type === 'pen' && (!newEl.pts || newEl.pts.length < 2)) { applyDrawing(null); return; }
    if ((newEl.type === 'line' || newEl.type === 'arrow') &&
        Math.hypot((newEl.x2 ?? 0) - (newEl.x ?? 0), (newEl.y2 ?? 0) - (newEl.y ?? 0)) < 5) { applyDrawing(null); return; }

    mutate(els => [...els, newEl]);
    applyDrawing(null);
  }, [elements, mutate, scheduleSave, marquee, getBBox, applyDrawing]);

  // ── Element interactions ──
  const onElDown = useCallback((e: React.MouseEvent, id: string): void => {
    if (tool === 'eraser') { mutate(els => els.filter(el => el.id !== id)); return; }
    if (tool !== 'select') return;

    // Shift-click toggles the clicked element in the multi-selection.
    if (e.shiftKey) {
      e.stopPropagation();
      if (id === selectedId) {
        // Demote the focused one — pick someone else from multi, or clear.
        const next = new Set(multiIds);
        const iter = next.values().next();
        if (iter.done) setSelectedId(null);
        else {
          next.delete(iter.value);
          setSelectedId(iter.value);
          setMultiIds(next);
        }
      } else if (multiIds.has(id)) {
        const next = new Set(multiIds);
        next.delete(id);
        setMultiIds(next);
      } else {
        // Add to selection: previous focus becomes part of multi.
        if (selectedId) setMultiIds(prev => new Set(prev).add(selectedId));
        setSelectedId(id);
      }
      return;
    }

    // Was the click on an already-selected element? → keep the group
    // selected and drag all of them together. Otherwise it's a fresh
    // single-click: reset to solo selection of this element.
    const alreadySelected = id === selectedId || multiIds.has(id);
    const ids = alreadySelected ? [selectedId, ...multiIds].filter((v): v is string => typeof v === 'string' && v.length > 0) : [id];
    if (!alreadySelected) {
      setSelectedId(id);
      setMultiIds(new Set());
      setLockedGroup(false);
    }
    const origs: Record<string, MutableWhiteboardElement> = {};
    for (const eid of ids) {
      const orig = elements.find(el => el.id === eid);
      if (orig) origs[eid] = JSON.parse(JSON.stringify(orig));
    }
    const finalIds = Object.keys(origs);
    if (finalIds.length === 0) return;
    if (!beginPointer(e)) return;
    dragRef.current = { ids: finalIds, startPt: toSvgPt(e), origs };
  }, [tool, elements, toSvgPt, mutate, selectedId, multiIds, beginPointer]);

  const onElDblClick = useCallback((id: string): void => {
    const el = elements.find(e => e.id === id);
    if (!el) return;
    if (el.type === 'sticky') setEditingEl({ ...el });
    if (el.type === 'image') setCropTarget({ ...el } as ImageElement);
    if (['rect','ellipse','diamond','triangle'].includes(el.type)) setEditingEl({ ...el });
  }, [elements]);

  const onResizeHandleDown = useCallback((e: React.MouseEvent, id: string, handle: string): void => {
    const orig = elements.find(el => el.id === id);
    if (!orig) return;
    if (!beginPointer(e)) return;
    resizeRef.current = { id, handle, startPt: toSvgPt(e), orig: JSON.parse(JSON.stringify(orig)) };
  }, [elements, toSvgPt, beginPointer]);

  // ── Image import confirm ──
  const handleImageInsert = useCallback((src: string): void => {
    setShowImgModal(false);
    const img = new window.Image();
    img.onload = (): void => {
      const maxW = 300;
      const scale = Math.min(1, maxW / img.naturalWidth);
      const w = Math.round(img.naturalWidth * scale);
      const h = Math.round(img.naturalHeight * scale);
      const pt = pendingImgPt || { x: 100, y: 100 };
      const newEl: MutableWhiteboardElement = { id: uid(), type: 'image', src, x: pt.x - w/2, y: pt.y - h/2, w, h };
      mutate(els => [...els, newEl]);
      setSelectedId(newEl.id);
      setTool('select');
    };
    img.onerror = (): void => {
      const newEl: MutableWhiteboardElement = { id: uid(), type: 'image', src, x: (pendingImgPt?.x||100)-100, y: (pendingImgPt?.y||100)-75, w: 200, h: 150 };
      mutate(els => [...els, newEl]);
      setSelectedId(newEl.id);
      setTool('select');
    };
    img.src = src;
    setPendingImgPt(null);
  }, [pendingImgPt, mutate]);

  // ── Crop confirm ──
  const handleCropConfirm = useCallback((newSrc: string, nw: number, nh: number): void => {
    if (!cropTarget) return;
    mutate(els => els.map(el => {
      if (el.id !== cropTarget.id) return el;
      const scale = Math.min(1, 300 / nw);
      return { ...el, src: newSrc, w: Math.round(nw*scale), h: Math.round(nh*scale) };
    }));
    setCropTarget(null);
  }, [cropTarget, mutate]);

  // Arrow markers
  const arrowColors = [...new Set([
    ...elements.filter((e): e is ArrowElement => e.type === 'arrow' && !!e.stroke).map(e => e.stroke as string),
    ...(drawing?.type === 'arrow' && drawing.stroke ? [drawing.stroke] : []),
  ])];

  const svgW = size.w;
  const svgH = size.h;
  const viewBoxStr = `${vb.x} ${vb.y} ${(svgW/vb.scale).toFixed(1)} ${(svgH/vb.scale).toFixed(1)}`;
  const isShapeTool = ['rect','ellipse','diamond','triangle'].includes(tool);
  const cursor = tool==='select' ? 'default'
    : tool==='hand' ? (panRef.current ? 'grabbing' : 'grab')
    : tool==='eraser' ? 'cell'
    : 'crosshair';

  const selectedEl = elements.find(e => e.id === selectedId);

  return (
    <div style={{display:'flex',width:'100%',height:'100%',position:'relative',background:'#f0f0eb',overflow:'hidden',touchAction:'none'}}>

      {/* ── Left toolbar ── */}
      <div style={{width:46,flexShrink:0,background:'#ffffff',borderRight:'1px solid #e2e2e2',
        display:'flex',flexDirection:'column',alignItems:'center',
        padding:'6px 0',gap:1,zIndex:20,boxShadow:'2px 0 8px rgba(0,0,0,0.07)',
        overflowY:'auto',overflowX:'hidden'}}>

        {TOOLS.map((t, i) => {
          if (t === null) return <div key={i} style={{width:'70%',height:1,background:'#ebebeb',margin:'3px 0',flexShrink:0}} />;
          const active = tool === t.id;
          return (
            <button key={t.id} title={t.title} onClick={(): void => setTool(t.id)}
              style={{width:34,height:32,border:'none',borderRadius:7,display:'flex',flexShrink:0,
                alignItems:'center',justifyContent:'center',cursor:'pointer',
                background: active ? '#eff6ff' : 'transparent',
                color: active ? '#3b82f6' : '#555',
                outline: active ? '2px solid #3b82f6' : 'none',
                transition:'background .12s'}}>
              {t.svg}
            </button>
          );
        })}

        <div style={{width:'70%',height:1,background:'#ebebeb',margin:'3px 0',flexShrink:0}} />

        {/* Undo / Redo */}
        <button title="Annuler (⌘Z)" onClick={undo} disabled={!canUndo}
          style={{width:34,height:32,border:'none',borderRadius:7,display:'flex',flexShrink:0,
            alignItems:'center',justifyContent:'center',
            cursor: canUndo ? 'pointer' : 'default',
            background:'transparent',
            color: canUndo ? '#555' : '#cbd5e1',
            transition:'background .12s'}}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 7v6h6"/>
            <path d="M21 17a9 9 0 0 0-15-6.7L3 13"/>
          </svg>
        </button>
        <button title="Rétablir (⌘⇧Z)" onClick={redo} disabled={!canRedo}
          style={{width:34,height:32,border:'none',borderRadius:7,display:'flex',flexShrink:0,
            alignItems:'center',justifyContent:'center',
            cursor: canRedo ? 'pointer' : 'default',
            background:'transparent',
            color: canRedo ? '#555' : '#cbd5e1',
            transition:'background .12s'}}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 7v6h-6"/>
            <path d="M3 17a9 9 0 0 1 15-6.7L21 13"/>
          </svg>
        </button>

        <div style={{width:'70%',height:1,background:'#ebebeb',margin:'3px 0',flexShrink:0}} />

        {/* Pen / stroke sizes */}
        {PEN_SIZES.map(({ s, label }) => (
          <button key={s} title={label} onClick={(): void => setSw(s)}
            style={{width:34,height:26,border:'none',borderRadius:6,display:'flex',flexShrink:0,
              alignItems:'center',justifyContent:'center',cursor:'pointer',
              background: sw===s ? '#eff6ff' : 'transparent'}}>
            <div style={{width:18,height:s,borderRadius:s,background:stroke}} />
          </button>
        ))}

        {/* Sticky colors */}
        {tool === 'sticky' && (
          <>
            <div style={{width:'70%',height:1,background:'#ebebeb',margin:'3px 0',flexShrink:0}} />
            {STICKY_BG.map(c => (
              <div key={c} onClick={(): void => setStickyBg(c)}
                style={{width:20,height:20,borderRadius:5,background:c,cursor:'pointer',flexShrink:0,
                  border: stickyBg===c ? '2.5px solid #3b82f6' : '1.5px solid rgba(0,0,0,0.12)'}} />
            ))}
          </>
        )}
      </div>

      {/* ── SVG canvas ── */}
      <svg ref={svgRef} style={{flex:1,cursor,display:'block',touchAction:'none'}}
        viewBox={viewBoxStr}
        onPointerDown={onSvgDown}
        onPointerMove={onSvgMove}
        onPointerUp={onSvgUp}
        onPointerCancel={onSvgUp}>

        <defs>
          <pattern id="wb-dots" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.9" fill="#c8c8c8" />
          </pattern>
          {arrowColors.map(c => (
            <marker key={c} id={`wbah-${c.replace('#','')}`}
              markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
              <polygon points="0 0, 8 3, 0 6" fill={c} />
            </marker>
          ))}
        </defs>

        <rect x={vb.x-4000} y={vb.y-4000} width={svgW/vb.scale+8000} height={svgH/vb.scale+8000} fill="white" />
        <rect x={vb.x-4000} y={vb.y-4000} width={svgW/vb.scale+8000} height={svgH/vb.scale+8000} fill="url(#wb-dots)" />

        {elements.map(el => (
          <RenderEl key={el.id} el={el} selected={el.id===selectedId || multiIds.has(el.id)}
            onDown={onElDown} onDblClick={onElDblClick}
            isSelectMode={tool==='select'}
            onResizeHandleDown={onResizeHandleDown} />
        ))}

        {marquee && (() => {
          const mx = Math.min(marquee.x, marquee.x2);
          const my = Math.min(marquee.y, marquee.y2);
          const mw = Math.abs(marquee.x2 - marquee.x);
          const mh = Math.abs(marquee.y2 - marquee.y);
          return (
            <rect x={mx} y={my} width={mw} height={mh}
              fill="rgba(59,130,246,0.08)" stroke="#3b82f6"
              strokeWidth={1 / vb.scale} strokeDasharray={`${4 / vb.scale} ${3 / vb.scale}`}
              pointerEvents="none" />
          );
        })()}

        {/* Group frame: drawn around the union of the multi-selection.
            Dashed while unlocked (decorative — individual items still grab
            clicks); solid and pointer-blocking while locked (drag here to
            move the whole group; clicks on individual items are absorbed). */}
        {multiSelectActive && selectionBBox && tool === 'select' && (
          <rect
            x={selectionBBox.x - 6 / vb.scale}
            y={selectionBBox.y - 6 / vb.scale}
            width={selectionBBox.w + 12 / vb.scale}
            height={selectionBBox.h + 12 / vb.scale}
            fill={lockedGroup ? 'rgba(59,130,246,0.04)' : 'transparent'}
            stroke="#3b82f6"
            strokeWidth={(lockedGroup ? 1.8 : 1.2) / vb.scale}
            strokeDasharray={lockedGroup ? undefined : `${6 / vb.scale} ${4 / vb.scale}`}
            rx={3 / vb.scale}
            pointerEvents={lockedGroup ? 'all' : 'none'}
            style={{ cursor: lockedGroup ? 'move' : undefined }}
            onPointerDown={lockedGroup ? startGroupDrag : undefined}
          />
        )}

        <DrawingGhost el={drawing} />
      </svg>

      {/* ── Bottom color palette ── */}
      <div style={{position:'absolute',bottom:14,left:'50%',transform:'translateX(-50%)',
        display:'flex',alignItems:'center',gap:7,background:'#fff',borderRadius:14,
        padding:'7px 14px',boxShadow:'0 4px 18px rgba(0,0,0,0.14)',zIndex:20}}>
        {PALETTE.map(c => (
          <div key={c} onClick={(): void => { setStroke(c); if (isShapeTool) setFill(c); }}
            style={{width:22,height:22,borderRadius:'50%',background:c,cursor:'pointer',
              border: stroke===c ? '2.5px solid #3b82f6' : c==='#ffffff' ? '1.5px solid #ccc' : '2px solid transparent',
              boxShadow: stroke===c ? '0 0 0 2px #93c5fd' : 'none',
              flexShrink:0,transition:'box-shadow .1s'}} />
        ))}
        {isShapeTool && (
          <>
            <div style={{width:1,height:22,background:'#e0e0e0',margin:'0 2px'}} />
            <div title="Remplissage / transparent" onClick={(): void => setFill(f => f==='none' ? stroke : 'none')}
              style={{width:22,height:22,borderRadius:'50%',cursor:'pointer',
                border:'2px solid #94a3b8',
                background: fill==='none'
                  ? 'repeating-linear-gradient(45deg,#ccc 0,#ccc 2px,transparent 0,transparent 50%)'
                  : fill,
                backgroundSize:'6px 6px'}} />
            <span style={{fontSize:10,color:'#888',whiteSpace:'nowrap'}}>remplissage</span>
          </>
        )}
      </div>

      {/* ── Zoom controls ── */}
      <div style={{position:'absolute',bottom:14,right:18,display:'flex',alignItems:'center',
        gap:6,background:'#fff',borderRadius:8,padding:'4px 10px',
        boxShadow:'0 2px 8px rgba(0,0,0,0.1)',zIndex:20,fontSize:11,color:'#666'}}>
        <button onClick={(): void => setVb(v => ({...v, scale: Math.max(0.15,v.scale/1.2)}))}
          style={{border:'none',background:'none',cursor:'pointer',fontSize:16,lineHeight:1,color:'#555'}}>−</button>
        <span style={{minWidth:36,textAlign:'center'}}>{Math.round(vb.scale*100)}%</span>
        <button onClick={(): void => setVb(v => ({...v, scale: Math.min(5,v.scale*1.2)}))}
          style={{border:'none',background:'none',cursor:'pointer',fontSize:16,lineHeight:1,color:'#555'}}>＋</button>
        <div style={{width:1,height:16,background:'#e0e0e0'}} />
        <button onClick={(): void => setVb({x:0,y:0,scale:1})}
          style={{border:'none',background:'none',cursor:'pointer',fontSize:11,color:'#3b82f6'}}>Reset</button>
      </div>

      {/* ── Properties panel (top-centre) for selected shape ── */}
      {selectedId && selectedEl && ['rect','ellipse','diamond','triangle','line','arrow','pen'].includes(selectedEl.type) && (
        <div style={{position:'absolute',top:10,left:'50%',transform:'translateX(-50%)',
          display:'flex',alignItems:'center',gap:8,background:'#fff',borderRadius:12,
          padding:'6px 14px',boxShadow:'0 4px 18px rgba(0,0,0,0.14)',zIndex:25,
          flexWrap:'wrap',maxWidth:'calc(100% - 80px)'}}>

          {/* Stroke color */}
          <span style={{fontSize:10,color:'#999',whiteSpace:'nowrap'}}>Trait</span>
          <div style={{display:'flex',gap:4,alignItems:'center'}}>
            {PALETTE.map(c => (
              <div key={c} onClick={(): void => mutate(els => els.map(el => el.id===selectedId ? {...el,stroke:c} : el))}
                style={{width:15,height:15,borderRadius:'50%',background:c,cursor:'pointer',
                  border: selectedEl.stroke===c ? '2px solid #3b82f6' : c==='#ffffff' ? '1px solid #ccc' : '1.5px solid transparent',
                  boxShadow: selectedEl.stroke===c ? '0 0 0 2px #93c5fd' : 'none',flexShrink:0}} />
            ))}
          </div>

          {/* Fill (shapes only) */}
          {['rect','ellipse','diamond','triangle'].includes(selectedEl.type) && (
            <>
              <div style={{width:1,height:18,background:'#e5e5e5'}} />
              <span style={{fontSize:10,color:'#999',whiteSpace:'nowrap'}}>Fond</span>
              <div style={{display:'flex',gap:4,alignItems:'center'}}>
                {/* No fill */}
                <div onClick={(): void => mutate(els => els.map(el => el.id===selectedId ? {...el,fill:'none'} : el))}
                  title="Transparent"
                  style={{width:15,height:15,borderRadius:'50%',cursor:'pointer',flexShrink:0,
                    border: (!selectedEl.fill||selectedEl.fill==='none') ? '2px solid #3b82f6' : '1.5px solid #ccc',
                    background:'repeating-linear-gradient(45deg,#ccc 0,#ccc 1.5px,transparent 0,transparent 50%)',
                    backgroundSize:'4px 4px'}} />
                {PALETTE.map(c => (
                  <div key={c} onClick={(): void => mutate(els => els.map(el => el.id===selectedId ? {...el,fill:c} : el))}
                    style={{width:15,height:15,borderRadius:'50%',background:c,cursor:'pointer',flexShrink:0,
                      border: selectedEl.fill===c ? '2px solid #3b82f6' : c==='#ffffff' ? '1px solid #ccc' : '1.5px solid transparent',
                      boxShadow: selectedEl.fill===c ? '0 0 0 2px #93c5fd' : 'none'}} />
                ))}
              </div>
            </>
          )}

          {/* Stroke width */}
          <div style={{width:1,height:18,background:'#e5e5e5'}} />
          <div style={{display:'flex',gap:4,alignItems:'center'}}>
            {PEN_SIZES.map(({s,label}) => (
              <div key={s} title={label} onClick={(): void => mutate(els => els.map(el => el.id===selectedId ? {...el,sw:s} : el))}
                style={{width:26,height:20,display:'flex',alignItems:'center',justifyContent:'center',
                  cursor:'pointer',borderRadius:5,
                  background: selectedEl.sw===s ? '#eff6ff' : 'transparent',
                  border: selectedEl.sw===s ? '1.5px solid #3b82f6' : '1.5px solid transparent'}}>
                <div style={{width:18,height:s,borderRadius:s,background:selectedEl.stroke||'#1a1a1a'}} />
              </div>
            ))}
          </div>

          {/* Stroke style: solid / dashed / dotted.
              Stored as a semantic key ('dashed' | 'dotted' | undefined); the
              actual SVG dasharray is computed at render time so it scales
              with stroke-width (thick strokes otherwise swallow tight gaps). */}
          <div style={{width:1,height:18,background:'#e5e5e5'}} />
          <div style={{display:'flex',gap:4,alignItems:'center'}}>
            {([
              { key: 'solid',  title: 'Continu',    value: undefined,  preview: <div style={{width:24,height:3,background:selectedEl.stroke||'#1a1a1a',borderRadius:2}} /> },
              { key: 'dashed', title: 'Tirets',     value: 'dashed',   preview: <div style={{display:'flex',gap:3,alignItems:'center'}}>{[0,1].map(i => <div key={i} style={{width:8,height:3,background:selectedEl.stroke||'#1a1a1a',borderRadius:1}}/>)}</div> },
              { key: 'dotted', title: 'Pointillés', value: 'dotted',   preview: <div style={{display:'flex',gap:3.5,alignItems:'center'}}>{[0,1,2,3].map(i => <div key={i} style={{width:3,height:3,borderRadius:'50%',background:selectedEl.stroke||'#1a1a1a'}}/>)}</div> },
            ] as { key: string; title: string; value: 'dashed' | 'dotted' | undefined; preview: React.ReactElement }[]).map(({ key, title, value, preview }) => {
              const active = (selectedEl as { dash?: string }).dash === value;
              return (
                <div key={key} title={title}
                  onClick={(): void => mutate(els => els.map(el => el.id===selectedId ? { ...el, dash: value } : el))}
                  style={{width:30,height:20,display:'flex',alignItems:'center',justifyContent:'center',
                    cursor:'pointer',borderRadius:5,
                    background: active ? '#eff6ff' : 'transparent',
                    border: active ? '1.5px solid #3b82f6' : '1.5px solid transparent'}}>
                  {preview}
                </div>
              );
            })}
          </div>

          {/* ── Typography (shapes with text) ── */}
          {['rect','ellipse','diamond','triangle'].includes(selectedEl.type) && selectedEl.text && (
            <>
              <div style={{width:1,height:18,background:'#e5e5e5'}} />

              {/* Font size */}
              <select value={selectedEl.fontSize||13}
                onChange={(e): void => mutate(els => els.map(el => el.id===selectedId ? {...el,fontSize:+e.target.value} : el))}
                style={{fontSize:11,border:'1px solid #ddd',borderRadius:5,padding:'2px 4px',cursor:'pointer',outline:'none',height:24}}>
                {[9,10,11,12,13,14,16,18,20,24,28,32,40,48,64].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>

              {/* Font family */}
              <select value={selectedEl.fontFamily||'sans-serif'}
                onChange={(e): void => mutate(els => els.map(el => el.id===selectedId ? {...el,fontFamily:e.target.value} : el))}
                style={{fontSize:11,border:'1px solid #ddd',borderRadius:5,padding:'2px 4px',cursor:'pointer',outline:'none',height:24,maxWidth:110}}>
                {[
                  ['sans-serif','Sans-serif'],['serif','Serif'],['monospace','Mono'],
                  ['cursive','Cursive'],['Georgia','Georgia'],['Arial','Arial'],
                  ['Times New Roman','Times'],['Courier New','Courier'],
                  ['Trebuchet MS','Trebuchet'],['Impact','Impact'],
                ].map(([val,label]) => <option key={val} value={val}>{label}</option>)}
              </select>

              {/* Bold / Italic / Underline */}
              {[
                { key:'fontWeight',   on:'bold',      off:'normal', label:'B', style:{fontWeight:'bold'} },
                { key:'fontStyle',    on:'italic',    off:'normal', label:'I', style:{fontStyle:'italic'} },
                { key:'textDecoration',on:'underline', off:'none',  label:'U', style:{textDecoration:'underline'} },
              ].map(({ key, on, off, label, style: btnStyle }) => {
                const active = ((selectedEl as unknown as Record<string, string | undefined>)[key]||off) === on;
                return (
                  <button key={key}
                    onClick={(): void => mutate(els => els.map(el => el.id===selectedId ? {...el,[key]: active ? off : on} : el))}
                    style={{width:22,height:22,border: active ? '1.5px solid #3b82f6' : '1.5px solid #ddd',
                      borderRadius:5,background: active ? '#eff6ff' : '#fff',
                      color: active ? '#3b82f6' : '#555',cursor:'pointer',
                      fontSize:12,display:'flex',alignItems:'center',justifyContent:'center',
                      flexShrink:0,...btnStyle}}>
                    {label}
                  </button>
                );
              })}

              {/* Text color */}
              <div style={{width:1,height:18,background:'#e5e5e5'}} />
              <span style={{fontSize:10,color:'#999',whiteSpace:'nowrap'}}>A</span>
              <div style={{display:'flex',gap:3,alignItems:'center'}}>
                {['#1a1a1a','#ffffff','#ef4444','#f97316','#eab308','#22c55e','#3b82f6','#8b5cf6'].map(c => (
                  <div key={c} onClick={(): void => mutate(els => els.map(el => el.id===selectedId ? {...el,textColor:c} : el))}
                    style={{width:14,height:14,borderRadius:'50%',background:c,cursor:'pointer',flexShrink:0,
                      border: (selectedEl.textColor||'#1a1a1a')===c ? '2px solid #3b82f6' : c==='#ffffff' ? '1px solid #ccc' : '1.5px solid transparent'}} />
                ))}
              </div>
            </>
          )}

          {/* Hint for shapes without text */}
          {['rect','ellipse','diamond','triangle'].includes(selectedEl.type) && !selectedEl.text && (
            <>
              <div style={{width:1,height:18,background:'#e5e5e5'}} />
              <span style={{fontSize:10,color:'#bbb',fontStyle:'italic'}}>↩ double-clic pour écrire</span>
            </>
          )}
        </div>
      )}

      {/* ── Action bar (top-right) for selected element ── */}
      {selectedId && !multiSelectActive && (
        <div style={{position:'absolute',top:12,right:18,zIndex:20,
          display:'flex',gap:8,alignItems:'center'}}>
          {selectedEl?.type === 'image' && (
            <>
              <button onClick={(): void => mutate(els => els.map(el =>
                el.id === selectedId ? { ...el, rotate: ((el.rotate??0) + 90) % 360 } : el))}
                style={{padding:'5px 14px',border:'1px solid #d1d5db',borderRadius:7,
                  background:'#fff',color:'#374151',cursor:'pointer',fontSize:12,
                  boxShadow:'0 2px 8px rgba(0,0,0,0.08)'}}>
                ↻ 90°
              </button>
              <button onClick={(): void => setCropTarget({ ...selectedEl } as ImageElement)}
                style={{padding:'5px 14px',border:'1px solid #93c5fd',borderRadius:7,
                  background:'#fff',color:'#3b82f6',cursor:'pointer',fontSize:12,
                  boxShadow:'0 2px 8px rgba(0,0,0,0.08)'}}>
                ✂️ Rogner
              </button>
            </>
          )}
          <button onClick={(): void => { mutate(els => els.filter(el => el.id !== selectedId)); setSelectedId(null); }}
            style={{padding:'5px 14px',border:'1px solid #fca5a5',borderRadius:7,
              background:'#fff',color:'#ef4444',cursor:'pointer',fontSize:12,
              boxShadow:'0 2px 8px rgba(0,0,0,0.08)'}}>
            🗑 Supprimer
          </button>
        </div>
      )}

      {/* ── Group action bar (top-centre) for multi-selection ──
           Same visual treatment as the single-element "Trait" properties
           panel so the two states feel like one unified top bar. */}
      {multiSelectActive && (
        <div style={{position:'absolute',top:10,left:'50%',transform:'translateX(-50%)',
          display:'flex',alignItems:'center',gap:10,background:'#fff',borderRadius:12,
          padding:'6px 14px',boxShadow:'0 4px 18px rgba(0,0,0,0.14)',zIndex:25,
          flexWrap:'wrap',maxWidth:'calc(100% - 80px)'}}>
          <span style={{fontSize:10,color:'#999',whiteSpace:'nowrap'}}>Sélection</span>
          <span style={{fontSize:12,color:'#374151',fontWeight:600,whiteSpace:'nowrap'}}>
            {(selectedId ? 1 : 0) + multiIds.size} éléments
          </span>
          <div style={{width:1,height:18,background:'#e5e5e5'}} />
          <button onClick={(): void => setLockedGroup(v => !v)}
            title={lockedGroup ? 'Cliquer pour dégrouper et resélectionner individuellement' : 'Regrouper la sélection — clic-glisse le cadre bleu pour déplacer l\'ensemble'}
            style={{padding:'5px 12px',border: lockedGroup ? '1px solid #3b82f6' : '1px solid #d1d5db',
              borderRadius:7,
              background: lockedGroup ? '#3b82f6' : '#fff',
              color: lockedGroup ? '#fff' : '#374151',
              cursor:'pointer',fontSize:12,fontWeight:600,
              display:'flex',alignItems:'center',gap:4,whiteSpace:'nowrap'}}>
            {lockedGroup ? '🔓 Dégrouper' : '🔒 Regrouper'}
          </button>
          <button onClick={(): void => {
              const ids = new Set<string>(multiIds);
              if (selectedId) ids.add(selectedId);
              mutate(els => els.filter(el => !ids.has(el.id)));
              clearSelection();
            }}
            style={{padding:'5px 12px',border:'1px solid #fca5a5',borderRadius:7,
              background:'#fff',color:'#ef4444',cursor:'pointer',fontSize:12,whiteSpace:'nowrap'}}>
            🗑 Supprimer
          </button>
          <button onClick={clearSelection}
            title="Désélectionner (Esc)"
            style={{padding:'5px 10px',border:'1px solid #e5e7eb',borderRadius:7,
              background:'#fff',color:'#6b7280',cursor:'pointer',fontSize:12}}>
            ✖
          </button>
        </div>
      )}

      {/* ── Modals ── */}
      {showImgModal && (
        <ImageImportModal
          onConfirm={handleImageInsert}
          onClose={(): void => { setShowImgModal(false); setPendingImgPt(null); setTool('select'); }}
        />
      )}

      {cropTarget && (
        <CropModal
          el={cropTarget}
          onConfirm={handleCropConfirm}
          onClose={(): void => setCropTarget(null)}
        />
      )}

      {editingEl && (
        <StickyEditor
          el={editingEl}
          onSave={(text): void => {
            mutate(els => els.map(el => el.id===editingEl.id ? {...el, text} : el));
            setEditingEl(null);
          }}
          onClose={(): void => setEditingEl(null)}
        />
      )}
    </div>
  );
}
